/**
 * Module Registry Utilities
 * 
 * Utility functions and classes for module registry operations
 */

export { ModuleCache } from './ModuleCache'
export type { CacheOptions, CacheStats } from './ModuleCache'

export { BrowserEventEmitter, defaultEventEmitter } from './BrowserEventEmitter'
export type { EventListener, EventEmitterInterface } from './BrowserEventEmitter'

// Platform modules
export {
  getPath,
  getFs,
  getGlob,
  initializePlatformModules,
  preloadBrowserModules,
  getPlatformInfo,
  createPlatformError,
  clearModuleCache
} from './platform-modules'
export type { PlatformPath, PlatformFs, PlatformGlob } from './platform-modules'

// Environment utilities
export {
  detectEnvironment,
  loadConditionalModule,
  loadConditionalModuleSync,
  getPathSeparator,
  getCurrentDirectory,
  isDevelopment,
  isProduction,
  isTest,
  isBrowser,
  isNode,
  getEnvironmentName,
  getPlatformName,
  createLogger,
  hasFeature,
  assertFeature,
  warnMissingFeature,
  ENV,
  FEATURES
} from './environment'
export type { EnvironmentInfo } from './environment'

// Browser compatibility modules (classes only - instances loaded dynamically)
export type { BrowserPath } from './browser-path'
export type { BrowserFileSystem } from './browser-fs'
export type { BrowserGlob } from './browser-glob'

// Module loading utilities
export const loadModuleAsync = async (modulePath: string) => {
  try {
    const module = await import(modulePath)
    return module.default || module
  } catch (error) {
    console.error(`Failed to load module from ${modulePath}:`, error)
    throw error
  }
}

// Module ID utilities
export const normalizeModuleId = (id: string): string => {
  return id.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
}

export const isValidModuleId = (id: string): boolean => {
  return /^[a-z0-9-]+$/.test(id) && id.length > 0 && !id.startsWith('-') && !id.endsWith('-')
}

// Path utilities
export const resolveModulePath = (baseDir: string, moduleId: string): string => {
  return `${baseDir}/${moduleId}`
}

export const getModulePackageName = (moduleId: string): string => {
  return `@dbs-portal/module-${moduleId}`
}

// Permission utilities
export const hasPermission = (userPermissions: string[], requiredPermissions: string[]): boolean => {
  if (requiredPermissions.length === 0) return true
  return requiredPermissions.some(permission => userPermissions.includes(permission))
}

export const hasRole = (userRoles: string[], requiredRoles: string[]): boolean => {
  if (!requiredRoles || requiredRoles.length === 0) return true
  return requiredRoles.some(role => userRoles.includes(role))
}

// Module filtering utilities
export const filterModulesByPermissions = <T extends { permissions: string[] }>(
  modules: T[],
  userPermissions: string[]
): T[] => {
  return modules.filter(module => hasPermission(userPermissions, module.permissions))
}

export const filterModulesByRoles = <T extends { roles?: string[] }>(
  modules: T[],
  userRoles: string[]
): T[] => {
  return modules.filter(module => hasRole(userRoles, module.roles || []))
}

// Sorting utilities
export const sortModulesByPriority = <T extends { priority: number }>(modules: T[]): T[] => {
  return [...modules].sort((a, b) => b.priority - a.priority)
}

export const sortModulesByName = <T extends { name: string }>(modules: T[]): T[] => {
  return [...modules].sort((a, b) => a.name.localeCompare(b.name))
}

// Grouping utilities
export const groupModulesByCategory = <T extends { category: string }>(
  modules: T[]
): Record<string, T[]> => {
  return modules.reduce((groups, module) => {
    const category = module.category
    if (!groups[category]) {
      groups[category] = []
    }
    groups[category].push(module)
    return groups
  }, {} as Record<string, T[]>)
}

// Validation utilities
export const validateModuleId = (id: string): { valid: boolean; error?: string } => {
  if (!id) {
    return { valid: false, error: 'Module ID is required' }
  }
  
  if (!isValidModuleId(id)) {
    return { valid: false, error: 'Module ID must be lowercase alphanumeric with hyphens' }
  }
  
  if (id.length > 50) {
    return { valid: false, error: 'Module ID must be 50 characters or less' }
  }
  
  return { valid: true }
}

// Dependency utilities
export const resolveDependencies = <T extends { id: string; dependencies: string[] }>(
  modules: T[]
): T[] => {
  const moduleMap = new Map(modules.map(m => [m.id, m]))
  const resolved: T[] = []
  const resolving = new Set<string>()
  const resolved_ids = new Set<string>()

  const resolve = (moduleId: string): void => {
    if (resolved_ids.has(moduleId)) return
    if (resolving.has(moduleId)) {
      throw new Error(`Circular dependency detected: ${moduleId}`)
    }

    const module = moduleMap.get(moduleId)
    if (!module) {
      throw new Error(`Module not found: ${moduleId}`)
    }

    resolving.add(moduleId)

    // Resolve dependencies first
    for (const depId of module.dependencies) {
      resolve(depId)
    }

    resolving.delete(moduleId)
    resolved_ids.add(moduleId)
    resolved.push(module)
  }

  // Resolve all modules
  for (const module of modules) {
    resolve(module.id)
  }

  return resolved
}

// Error handling utilities
export class ModuleRegistryError extends Error {
  constructor(
    message: string,
    public readonly moduleId?: string,
    public override readonly cause?: Error
  ) {
    super(message)
    this.name = 'ModuleRegistryError'
  }
}

export class ModuleValidationError extends ModuleRegistryError {
  constructor(
    message: string,
    moduleId?: string,
    public readonly validationErrors?: Array<{ field: string; message: string }>
  ) {
    super(message, moduleId)
    this.name = 'ModuleValidationError'
  }
}

export class ModuleDiscoveryError extends ModuleRegistryError {
  constructor(
    message: string,
    public readonly source?: string,
    cause?: Error
  ) {
    super(message, undefined, cause)
    this.name = 'ModuleDiscoveryError'
  }
}

// Performance utilities
export const measurePerformance = async <T>(
  operation: () => Promise<T>,
  label: string
): Promise<{ result: T; duration: number }> => {
  const start = performance.now()
  const result = await operation()
  const duration = performance.now() - start
  
  console.debug(`${label} took ${duration.toFixed(2)}ms`)
  
  return { result, duration }
}

// Debounce utility for frequent operations
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    
    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

// Throttle utility for performance-sensitive operations
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, limit)
    }
  }
}
